import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmt, stateBadgeStyleObj } from "../../../services/erpApi";
import { StatCard, PageHeader, LoadingScreen, Modal, EmptyState } from "../../../components/admin/EssUi";
import { useToast } from "../../../components/admin/ToastContext";
import CustomerSelect from "../../../components/admin/CustomerSelect";
import { Plus, Trash2, Search, ClipboardList, Banknote, Hourglass, Receipt, Mail, Inbox, MessageCircle } from "lucide-react";

const PAGE_SIZE = 15;

const AdminPanelSalesOrdersPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [partnerFilter, setPartnerFilter] = useState("all");
  const [contractFilter, setContractFilter] = useState("all");
  const [page, setPage] = useState(1);

  const [view, setView] = useState(null);
  const [viewLines, setViewLines] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);

  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);

  const [deliverFor, setDeliverFor] = useState(null);
  const [deliverLoc, setDeliverLoc] = useState("");
  const [locations, setLocations] = useState([]);
  const [delivering, setDelivering] = useState(false);

  const [mailOpen, setMailOpen] = useState(false);
  const [mailTemplates, setMailTemplates] = useState([]);
  const [mailTemplateId, setMailTemplateId] = useState("");
  const [mailBusy, setMailBusy] = useState(false);

  const [waOpen, setWaOpen] = useState(false);
  const [waTemplates, setWaTemplates] = useState([]);
  const [waTemplateId, setWaTemplateId] = useState("");
  const [waBusy, setWaBusy] = useState(false);

  const norm = (r) => (r && typeof r === "object" && !Array.isArray(r) && "data" in r ? r.data : r);

  useEffect(() => { setPage(1); }, [tab, search, stateFilter, partnerFilter, contractFilter]);

  const load = async () => {
    try {
      const rows = await api.sales.list({ limit: 500 });
      setData(rows || []);
    } catch { setData([]); }
  };

  useEffect(() => {
    (async () => {
      await Promise.all([
        load(),
        api.partners.list({ customer_rank: 1, limit: 100, is_active: "true" }).catch(() => api.partners.list({ limit: 100, is_active: "true" }).catch(() => [])),
      ]).then(([, p]) => {
        const arr = Array.isArray(p) ? p : p?.data && Array.isArray(p.data) ? p.data : [];
        setPartners(arr || []);
      });
      setLoaded(true);
    })();
  }, []);

  const refresh = async () => {
    await load();
  };

  const openView = (row) => {
    navigate(`/admin-panel/sales/${row.id}`);
  };

  const openDeliver = async (row) => {
    setDeliverFor(row);
    setDeliverLoc("");
    try {
      const locs = await api.inventory.locations().catch(() => []);
      setLocations(locs || []);
    } catch {
      setLocations([]);
    }
  };

  const openSendEmail = async () => {
    if (!view) return;
    setMailOpen(true);
    setMailTemplateId("");
    setMailTemplates([]);
    try {
      const res = await api.mail.templates.list({ limit: 100, model: "sales_order" });
      const n = norm(res);
      const rows = Array.isArray(n) ? n : Array.isArray(n?.data) ? n.data : [];
      setMailTemplates(rows.filter((t) => t.is_active !== false && (t.channel_type === "email" || !t.channel_type)));
    } catch { setMailTemplates([]); }
  };

  const openSendWhatsApp = async () => {
    if (!view) return;
    setWaOpen(true);
    setWaTemplateId("");
    setWaTemplates([]);
    try {
      const res = await api.mail.templates.list({ limit: 100, model: "sales_order" });
      const n = norm(res);
      const rows = Array.isArray(n) ? n : Array.isArray(n?.data) ? n.data : [];
      setWaTemplates(rows.filter((t) => t.is_active !== false && t.channel_type === "whatsapp"));
    } catch { setWaTemplates([]); }
  };

  const submitDeliver = async () => {
    if (!deliverFor) return;
    setDelivering(true);
    try {
      await api.sales.deliver(deliverFor.id, { location_id: deliverLoc ? parseInt(deliverLoc) : undefined });
      toast("Order delivered and stock issued", "success");
      setDeliverFor(null);
      if (view?.id === deliverFor.id) await openView(view);
      await refresh();
    } catch (e) {
      toast(e.message || "Deliver failed", "error");
    }
    setDelivering(false);
  };

  const handleSendEmail = async () => {
    if (!view || !mailTemplateId) { toast("Select a template", "error"); return; }
    setMailBusy(true);
    try {
      await api.sales.sendEmail(view.id, { template_id: parseInt(mailTemplateId) });
      toast("Email queued for sending", "success");
      setMailOpen(false);
    } catch (e) {
      toast(e.message || "Send email failed", "error");
    }
    setMailBusy(false);
  };

  const handleSendWhatsApp = async () => {
    if (!view || !waTemplateId) { toast("Select a template", "error"); return; }
    setWaBusy(true);
    try {
      await api.sales.sendWhatsApp?.(view.id, { template_id: parseInt(waTemplateId) }) || await api.mail.bulk({ template_id: parseInt(waTemplateId), lead_ids: [view.partner_id], channel: "whatsapp" });
      toast("WhatsApp message queued for sending", "success");
      setWaOpen(false);
    } catch (e) {
      toast(e.message || "Send WhatsApp failed", "error");
    }
    setWaBusy(false);
  };

  const doAction = async (id, kind, label) => {
    try {
      if (kind === "confirm") await api.sales.confirm(id);
      if (kind === "cancel") await api.sales.cancel(id);
      if (kind === "invoice") await api.sales.invoice(id);
      toast(`${label} successful`, "success");
      setView(null);
      await refresh();
    } catch (e) {
      toast(e.message || `${label} failed`, "error");
    }
  };

  const newFormInit = () => ({
    partner_id: "",
    user_id: "",
    date_order: new Date().toISOString().split("T")[0],
    valid_until: "",
    lines: [],
  });
  const [form, setForm] = useState(newFormInit());

  const loadRefs = async () => {
    try {
      const [p, pr, u] = await Promise.all([
        api.partners.list({ customer_rank: 1, limit: 100, is_active: "true" }).catch(() => api.partners.list({ limit: 100, is_active: "true" }).catch(() => [])),
        api.products.list({ limit: 500 }).catch(() => []),
        api.users.list().catch(() => []),
      ]);
      const arr = Array.isArray(p) ? p : p?.data && Array.isArray(p.data) ? p.data : [];
      setPartners(Array.isArray(arr) ? arr : []);
      setProducts(pr || []);
      setUsers(u || []);
    } catch {}
  };

  const openNew = async () => {
    setForm(newFormInit());
    setShowNew(true);
    await loadRefs();
  };

  const addLine = () => {
    setForm((f) => ({ ...f, lines: [...f.lines, { product_id: "", product_uom_qty: 1, price_unit: "", discount: 0 }] }));
  };

  const setLine = (i, patch) => {
    setForm((f) => {
      const lines = f.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l));
      return { ...f, lines };
    });
  };

  const removeLine = (i) => {
    setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  };

  const pickProduct = (i, productId) => {
    const prod = products.find((p) => String(p.id) === String(productId));
    if (!prod) return;
    setLine(i, {
      product_id: productId,
      product_uom: prod.uom_id,
      price_unit: prod.list_price || 0,
      name: prod.name,
    });
  };

  const lineSubtotal = (l) => {
    const qty = Number(l.product_uom_qty) || 0;
    const price = Number(l.price_unit) || 0;
    const disc = Number(l.discount) || 0;
    return qty * price * (1 - disc / 100);
  };

  const orderTotals = useMemo(() => {
    const untaxed = form.lines.reduce((s, l) => s + lineSubtotal(l), 0);
    return { untaxed };
  }, [form.lines]);

  const handleCreate = async () => {
    if (!form.partner_id) { toast("Customer is required", "error"); return; }
    if (form.lines.length === 0) { toast("Add at least one product line", "error"); return; }
    const validLines = form.lines.filter((l) => l.product_id && (Number(l.product_uom_qty) || 0) > 0);
    if (validLines.length === 0) { toast("No valid product lines", "error"); return; }
    setCreating(true);
    try {
      const body = {
        partner_id: parseInt(form.partner_id),
        user_id: form.user_id ? parseInt(form.user_id) : undefined,
        date_order: form.date_order ? `${form.date_order}T00:00:00Z` : undefined,
        valid_until: form.valid_until ? `${form.valid_until}T00:00:00Z` : undefined,
        state: "draft",
        lines: validLines.map((l) => ({
          product_id: parseInt(l.product_id),
          product_uom_qty: Number(l.product_uom_qty),
          product_uom: l.product_uom ? parseInt(l.product_uom) : undefined,
          price_unit: Number(l.price_unit) || 0,
          discount: Number(l.discount) || 0,
          name: l.name,
        })),
      };
      const created = await api.sales.create(body);
      toast(`Sale Order ${created?.name || ""} created`, "success");
      setShowNew(false);
      await refresh();
    } catch (e) {
      toast(e.message || "Create failed", "error");
    }
    setCreating(false);
  };

  const customerName = (row) => row.partner_name || row.partner_id || "—";

  const contractStatus = (r) => {
    if (r.is_renewed) return "renewed";
    if (!r.valid_until) return "active";
    const now = new Date();
    const expiry = new Date(r.valid_until);
    const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return "expired";
    if (diffDays <= 30) return "expiring_soon";
    return "active";
  };

  const contractBadge = (r) => {
    const s = contractStatus(r);
    const map = {
      active: { text: "#225f2b", bg: "#f0f7f0", label: "Active" },
      expired: { text: "#be123c", bg: "#fff1f2", label: "Expired" },
      expiring_soon: { text: "#b45309", bg: "#fffbeb", label: "Expiring soon" },
      renewed: { text: "#0f766e", bg: "#eef6f3", label: "Renewed" },
    };
    const c = map[s] || map.active;
    return <span style={{ background: c.bg, color: c.text, padding: "2px 8px", borderRadius: 5, fontSize: 10, fontWeight: 700 }}>{c.label}</span>;
  };

  const stats = useMemo(() => {
    const active = data.filter((r) => r.state !== "cancel");
    const revenue = active.reduce((s, r) => s + (Number(r.amount_total) || 0), 0);
    const draft = data.filter((r) => r.state === "draft").length;
    const toInvoice = data.filter((r) => r.state === "sale" && r.invoice_status !== "invoiced").length;
    return { count: data.length, revenue, draft, toInvoice };
  }, [data]);

  const filtered = useMemo(() => {
    return data.filter((r) => {
      if (tab !== "all" && r.state !== tab) return false;
      if (stateFilter !== "all" && r.state !== stateFilter) return false;
      if (partnerFilter !== "all" && String(r.partner_id) !== partnerFilter) return false;
      if (contractFilter !== "all" && contractStatus(r) !== contractFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${r.name || ""} ${r.partner_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, tab, search, stateFilter, partnerFilter, contractFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const tabs = [
    { key: "all", label: "All", count: data.length },
    { key: "draft", label: "Draft", count: data.filter((r) => r.state === "draft").length },
    { key: "sale", label: "Sale", count: data.filter((r) => r.state === "sale").length },
    { key: "done", label: "Done", count: data.filter((r) => r.state === "done").length },
    { key: "cancel", label: "Cancelled", count: data.filter((r) => r.state === "cancel").length },
  ];

  const inputStyle = {
    width: "100%", padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px",
    fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box",
  };
  const labelStyle = { fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px", display: "block" };
  const FormField = ({ label, children }) => (
    <div style={{ marginBottom: "16px" }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );

  if (!loaded) return <LoadingScreen />;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader
        title="Sales Orders"
        subtitle="Manage customer orders, confirmations and invoicing"
        actions={
          <button onClick={openNew} className="ess-btn ess-btn-primary" style={{ padding: "9px 16px" }}>
            <Plus size={16} />
            New Order
          </button>
        }
      />

      <div className="ess-sg ess-sg4" style={{ marginBottom: "24px" }}>
        <StatCard label="Total Orders" value={String(stats.count)} icon={ClipboardList} color="green" trend={`${data.filter((r) => r.state !== "cancel").length} active`} />
        <StatCard label="Revenue" value={fmt.moneyShort(stats.revenue)} icon={Banknote} color="green" trend={fmt.money(stats.revenue)} />
        <StatCard label="Draft" value={String(stats.draft)} icon={Hourglass} color="amber" trend="Awaiting confirmation" />
        <StatCard label="To Invoice" value={String(stats.toInvoice)} icon={Receipt} color="violet" trend="Confirmed orders to invoice" />
      </div>

      <div style={{ display: "flex", gap: "24px", marginBottom: "20px", borderBottom: "1px solid #e2e7e2", overflowX: "auto" }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: "9px 2px", border: "none", background: "transparent",
            borderBottom: `2px solid ${tab === t.key ? "#225f2b" : "transparent"}`,
            marginBottom: -1, whiteSpace: "nowrap",
            color: tab === t.key ? "#225f2b" : "#64748b", fontSize: "13px",
            fontWeight: tab === t.key ? "600" : "500", cursor: "pointer",
            display: "flex", alignItems: "center", gap: "6px"
          }}>
            {t.label}
            <span style={{ background: tab === t.key ? "#f0f7f0" : "#f1f5f9", color: tab === t.key ? "#225f2b" : "#64748b", padding: "1px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: "600" }}>{t.count}</span>
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "24px", background: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e7e2" }}>
        <div style={{ flex: 1, minWidth: "200px", position: "relative" }}>
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94a3b8" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search order or customer..." style={{ ...inputStyle, padding: "9px 12px 9px 36px" }} />
        </div>
        <select value={partnerFilter} onChange={(e) => setPartnerFilter(e.target.value)} style={{ ...inputStyle, minWidth: "180px", cursor: "pointer" }}>
          <option value="all">All Customers</option>
          {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} style={{ ...inputStyle, minWidth: "160px", cursor: "pointer" }}>
          <option value="all">All Status</option>
          {["draft", "sale", "done", "cancel"].map((s) => (
            <option key={s} value={s}>{s === "cancel" ? "Cancelled" : s === "sale" ? "Sale" : s === "done" ? "Done" : "Draft"}</option>
          ))}
        </select>
        <select value={contractFilter} onChange={(e) => setContractFilter(e.target.value)} style={{ ...inputStyle, minWidth: "160px", cursor: "pointer" }}>
          <option value="all">All Contracts</option>
          <option value="active">Active</option>
          <option value="expiring_soon">Expiring soon</option>
          <option value="expired">Expired</option>
          <option value="renewed">Renewed</option>
        </select>
      </div>

      <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
        {paginated.length === 0 ? (
          <EmptyState icon={Inbox} title="No sales orders" subtitle="Create your first order with the New Order button" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th className="ess-th" style={{ minWidth: "110px" }}>Order #</th>
                  <th className="ess-th" style={{ minWidth: "160px" }}>Customer</th>
                  <th className="ess-th" style={{ minWidth: "110px" }}>Date</th>
                  <th className="ess-th" style={{ textAlign: "center", minWidth: "70px" }}>Lines</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "130px" }}>Total</th>
                  <th className="ess-th" style={{ minWidth: "100px" }}>Invoice</th>
                  <th className="ess-th" style={{ minWidth: "100px" }}>Status</th>
                  <th className="ess-th" style={{ minWidth: "110px" }}>Contract</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "70px" }}></th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((row) => (
                  <tr key={row.id} className="ess-tr">
                    <td className="ess-td" style={{ fontWeight: "600", color: "#225f2b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.name || `SO${String(row.id).padStart(5, "0")}`}</td>
                    <td className="ess-td" style={{ fontWeight: "500", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{customerName(row)}</td>
                    <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{fmt.date(row.date_order)}</td>
                    <td className="ess-td" style={{ color: "#475569", textAlign: "center", whiteSpace: "nowrap" }}>{row.line_count || 0}</td>
                    <td className="ess-td" style={{ fontWeight: "600", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(row.amount_total)}</td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                      <span style={{ ...stateBadgeStyleObj(row.invoice_status === "invoiced" ? "invoiced" : row.invoice_status === "paid" ? "paid" : "draft"), padding: "4px 12px", borderRadius: "12px", fontSize: "12px", fontWeight: "600" }}>
                        {row.invoice_status === "invoiced" ? "Invoiced" : row.invoice_status === "paid" ? "Paid" : row.state === "draft" ? "—" : "Not Invoiced"}
                      </span>
                    </td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                      <span style={stateBadgeStyleObj(row.state)}>{row.state === "cancel" ? "Cancelled" : row.state === "sale" ? "Sale" : row.state === "done" ? "Done" : "Draft"}</span>
                    </td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                      {row.valid_until ? contractBadge(row) : "—"}
                    </td>
                    <td className="ess-td" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button onClick={() => openView(row)} style={{ border: "none", background: "transparent", color: "#225f2b", fontSize: "13px", fontWeight: "500", cursor: "pointer", padding: "4px 8px", borderRadius: "4px", transition: "all 0.2s" }} onMouseEnter={(e) => (e.target.style.background = "#f0f7f0")} onMouseLeave={(e) => (e.target.style.background = "transparent")}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #e2e7e2", background: "#fafafa" }}>
          <span style={{ fontSize: "13px", color: "#64748b" }}>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div style={{ display: "flex", gap: "4px" }}>
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: page <= 1 ? "#f1f5f9" : "white", color: page <= 1 ? "#94a3b8" : "#334155", fontSize: "13px", fontWeight: "500", cursor: page <= 1 ? "not-allowed" : "pointer" }}>← Prev</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const start = Math.max(1, Math.min(page - 2, totalPages - 4));
              const p = start + i;
              if (p > totalPages) return null;
              return <button key={p} onClick={() => setPage(p)} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: p === page ? "#225f2b" : "white", color: p === page ? "white" : "#334155", fontSize: "13px", fontWeight: p === page ? "600" : "500", cursor: "pointer" }}>{p}</button>;
            })}
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: page >= totalPages ? "#f1f5f9" : "white", color: page >= totalPages ? "#94a3b8" : "#334155", fontSize: "13px", fontWeight: "500", cursor: page >= totalPages ? "not-allowed" : "pointer" }}>Next →</button>
          </div>
        </div>
      </div>

      <Modal open={!!view} onClose={() => setView(null)} title={view ? `Sale Order ${view.name || ""}` : ""} size="lg">
        {view && (
          <div>
            <div style={{ display: "grid", gap: "16px", marginBottom: "24px" }} className="ess-rg-2">
              <div><label style={labelStyle}>Customer</label><p style={{ fontSize: "14px", color: "#1e293b", fontWeight: "600" }}>{view.partner_name || "—"}</p></div>
              <div><label style={labelStyle}>Date</label><p style={{ fontSize: "14px", color: "#1e293b", fontWeight: "500" }}>{fmt.dateTime(view.date_order)}</p></div>
              {view.valid_until && <div><label style={labelStyle}>Valid Until</label><p style={{ fontSize: "14px", color: "#1e293b", fontWeight: "500" }}>{fmt.date(view.valid_until)}</p></div>}
              <div><label style={labelStyle}>Order #</label><p style={{ fontSize: "14px", color: "#1e293b", fontWeight: "500" }}>{view.name || `SO${view.id}`}</p></div>
              <div><label style={labelStyle}>Status</label><span style={stateBadgeStyleObj(view.state)}>{view.state === "cancel" ? "Cancelled" : view.state === "sale" ? "Sale" : view.state === "done" ? "Done" : "Draft"}</span></div>
              {view.partner_email && <div><label style={labelStyle}>Email</label><p style={{ fontSize: "14px", color: "#475569" }}>{view.partner_email}</p></div>}
              {view.partner_phone && <div><label style={labelStyle}>Phone</label><p style={{ fontSize: "14px", color: "#475569" }}>{view.partner_phone}</p></div>}
              {view.partner_city && <div><label style={labelStyle}>City</label><p style={{ fontSize: "14px", color: "#475569" }}>{view.partner_city}</p></div>}
              {view.partner_street && <div><label style={labelStyle}>Address</label><p style={{ fontSize: "14px", color: "#475569" }}>{view.partner_street}</p></div>}
            </div>

            <h4 style={{ fontSize: "14px", fontWeight: "600", color: "#1e293b", marginBottom: "12px" }}>Order Lines</h4>
            <div style={{ border: "1px solid #e2e8f0", borderRadius: "10px", overflow: "hidden", marginBottom: "20px" }}>
              {viewLines.length === 0 ? (
                <div style={{ padding: "24px", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>{viewLoading ? "Loading lines..." : "No lines"}</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                      <th style={{ textAlign: "left", padding: "10px 14px", fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" }}>Product</th>
                      <th style={{ textAlign: "right", padding: "10px 14px", fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" }}>Qty</th>
                      <th style={{ textAlign: "right", padding: "10px 14px", fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" }}>Unit Price</th>
                      <th style={{ textAlign: "right", padding: "10px 14px", fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" }}>Disc %</th>
                      <th style={{ textAlign: "right", padding: "10px 14px", fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" }}>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewLines.map((l, i) => (
                      <tr key={i} style={{ borderBottom: i < viewLines.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                        <td style={{ padding: "10px 14px", fontSize: "13px", color: "#1e293b", fontWeight: "500" }}>{l.product_name || l.name || "—"}</td>
                        <td style={{ padding: "10px 14px", fontSize: "13px", color: "#475569", textAlign: "right" }}>{fmt.qty(l.product_uom_qty)}</td>
                        <td style={{ padding: "10px 14px", fontSize: "13px", color: "#475569", textAlign: "right" }}>{fmt.money(l.price_unit)}</td>
                        <td style={{ padding: "10px 14px", fontSize: "13px", color: "#475569", textAlign: "right" }}>{l.discount ? `${l.discount}%` : "—"}</td>
                        <td style={{ padding: "10px 14px", fontSize: "13px", fontWeight: "600", color: "#1e293b", textAlign: "right" }}>{fmt.money(l.price_subtotal ?? l.price_unit * l.product_uom_qty)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-end", marginBottom: "20px" }}>
              <div style={{ fontSize: "13px", color: "#64748b" }}>Untaxed <span style={{ fontWeight: "600", color: "#1e293b", marginLeft: "12px" }}>{fmt.money(view.amount_untaxed ?? view.amount_total)}</span></div>
              <div style={{ fontSize: "13px", color: "#64748b" }}>Tax <span style={{ fontWeight: "600", color: "#1e293b", marginLeft: "12px" }}>{fmt.money(view.amount_tax)}</span></div>
              <div style={{ fontSize: "16px", fontWeight: "700", color: "#1e293b" }}>Total <span style={{ marginLeft: "12px" }}>{fmt.money(view.amount_total)}</span></div>
            </div>

            <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "20px", display: "flex", gap: "12px", justifyContent: "flex-end", flexWrap: "wrap" }}>
              {view.state === "draft" && (
                <>
                  <button onClick={() => doAction(view.id, "cancel", "Cancel order")} className="ess-btn ess-btn-danger" style={{ padding: "10px 24px" }}>Cancel</button>
                  <button onClick={() => doAction(view.id, "confirm", "Confirm order")} className="ess-btn ess-btn-primary" style={{ padding: "10px 24px" }}>Confirm</button>
                </>
              )}
              {view.state === "sale" && view.invoice_status !== "invoiced" && view.invoice_status !== "paid" && (
                <>
                  <button onClick={() => doAction(view.id, "cancel", "Cancel order")} className="ess-btn ess-btn-danger" style={{ padding: "10px 24px" }}>Cancel</button>
                  <button onClick={() => doAction(view.id, "invoice", "Create invoice")} className="ess-btn ess-btn-primary" style={{ padding: "10px 24px" }}>Create Invoice</button>
                </>
              )}
              {view.state === "sale" && (
                <button onClick={() => openDeliver(view)} className="ess-btn ess-btn-primary" style={{ padding: "10px 24px" }}>Deliver</button>
              )}
              {view.state === "cancel" && <p style={{ fontSize: "13px", color: "#e11d48", fontWeight: "500", padding: "10px 0" }}>This order has been cancelled.</p>}
              {(view.invoice_status === "invoiced" || view.invoice_status === "paid") && <p style={{ fontSize: "13px", color: "#16a34a", fontWeight: "500", padding: "10px 0" }}>Invoice created for this order.</p>}
              {(view.partner_email || view.partner_phone) && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {view.partner_email && (
                    <button onClick={openSendEmail} className="ess-btn ess-btn-secondary" style={{ padding: "10px 24px" }}>
                      <Mail size={15} /> Send by Email
                    </button>
                  )}
                  {view.partner_phone && (
                    <button onClick={openSendWhatsApp} className="ess-btn ess-btn-secondary" style={{ padding: "10px 24px", background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0" }}>
                      <MessageCircle size={15} /> Send by WhatsApp
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal open={showNew} onClose={() => setShowNew(false)} title="New Sale Order" size="lg">
        <div>
          <div style={{ display: "grid", gap: "16px" }} className="ess-rg-2">
            <FormField label="Customer *">
              <CustomerSelect value={form.partner_id} onChange={(id) => setForm((f) => ({ ...f, partner_id: id }))} placeholder="Search customer… (tape 2 lettres)" required />
            </FormField>
            <FormField label="Salesperson">
              <select value={form.user_id} onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))} style={inputStyle}>
                <option value="">—</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.employee_name || u.login}</option>)}
              </select>
            </FormField>
            <FormField label="Order Date">
              <input type="date" value={form.date_order} onChange={(e) => setForm((f) => ({ ...f, date_order: e.target.value }))} style={inputStyle} />
            </FormField>
            <FormField label="Valid Until (contract expiry)">
              <input type="date" value={form.valid_until} onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))} style={inputStyle} />
            </FormField>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "8px 0 12px" }}>
            <h4 style={{ fontSize: "14px", fontWeight: "600", color: "#1e293b" }}>Order Lines</h4>
            <button onClick={addLine} className="ess-btn ess-btn-secondary" style={{ padding: "4px 10px" }}>
              <Plus size={14} /> Add Line
            </button>
          </div>

          {form.lines.length === 0 && (
            <div className="ess-empty" style={{ marginBottom: "16px" }}>No lines yet — click "Add Line" to add a product</div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "20px" }}>
            {form.lines.map((l, i) => (
              <div key={i} style={{ display: "flex", gap: "8px", alignItems: "center", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "10px", flexWrap: "wrap" }}>
                <select value={l.product_id} onChange={(e) => pickProduct(i, e.target.value)} style={{ ...inputStyle, flex: "2", minWidth: "180px", background: "white" }}>
                  <option value="">Select product…</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}{p.default_code ? ` (${p.default_code})` : ""}</option>)}
                </select>
                <input type="number" min="0" value={l.product_uom_qty} onChange={(e) => setLine(i, { product_uom_qty: e.target.value })} placeholder="Qty" style={{ ...inputStyle, flex: "0 0 70px", background: "white" }} />
                <input type="number" min="0" value={l.price_unit} onChange={(e) => setLine(i, { price_unit: e.target.value })} placeholder="Price" style={{ ...inputStyle, flex: "0 0 100px", background: "white" }} />
                <input type="number" min="0" max="100" value={l.discount} onChange={(e) => setLine(i, { discount: e.target.value })} placeholder="Disc %" style={{ ...inputStyle, flex: "0 0 80px", background: "white" }} />
                <div style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b", flex: "0 0 90px", textAlign: "right" }}>{fmt.money(lineSubtotal(l))}</div>
                <button onClick={() => removeLine(i)} style={{ border: "none", background: "transparent", color: "#e11d48", cursor: "pointer", padding: "6px", flexShrink: 0 }} title="Remove line">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", padding: "14px 16px", background: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
            <span style={{ fontSize: "14px", color: "#475569" }}>Subtotal ({form.lines.length} lines)</span>
            <span style={{ fontSize: "16px", fontWeight: "700", color: "#1e293b" }}>{fmt.money(orderTotals.untaxed)}</span>
          </div>

          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e8f0", paddingTop: "20px" }}>
            <button onClick={() => setShowNew(false)} className="ess-btn ess-btn-secondary" style={{ padding: "10px 24px" }}>Cancel</button>
            <button onClick={handleCreate} disabled={creating} className="ess-btn ess-btn-primary" style={{ padding: "10px 24px" }}>
              {creating ? "Creating…" : "Create Order"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={!!deliverFor} onClose={() => setDeliverFor(null)} title={deliverFor ? `Deliver ${deliverFor.name || ""}` : ""}>
        {deliverFor && (
          <div>
            <p style={{ fontSize: "14px", color: "#475569", marginBottom: "20px" }}>
              Issue stock and mark <strong>{deliverFor.name || `SO${deliverFor.id}`}</strong> ({deliverFor.partner_name || "—"}) as delivered.
            </p>
            <FormField label="Destination Location (optional)">
              <select value={deliverLoc} onChange={(e) => setDeliverLoc(e.target.value)} style={inputStyle}>
                <option value="">Default customer location</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </FormField>
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e8f0", paddingTop: "20px" }}>
              <button onClick={() => setDeliverFor(null)} className="ess-btn ess-btn-secondary" style={{ padding: "10px 24px" }}>Cancel</button>
              <button onClick={submitDeliver} disabled={delivering} className="ess-btn ess-btn-primary" style={{ padding: "10px 24px" }}>
                {delivering ? "Delivering…" : "Confirm Delivery"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={mailOpen} onClose={() => setMailOpen(false)} title={view ? `Send Quotation — ${view.name || ""}` : "Send Quotation"} size="lg">
        <div>
          <FormField label="Template" required>
            {mailTemplates.length === 0 ? (
              <div style={{ fontSize: 13, color: "#94a3b8", padding: "9px 0" }}>No sales order email templates found.</div>
            ) : (
              <select value={mailTemplateId} onChange={(e) => setMailTemplateId(e.target.value)} style={inputStyle}>
                <option value="">Select a template…</option>
                {mailTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            )}
          </FormField>
          {(() => {
            const tpl = mailTemplates.find((t) => String(t.id) === String(mailTemplateId));
            if (!tpl) return null;
            return (
              <>
                <div style={{ fontSize: 13, color: "#334155", marginBottom: 8 }}>
                  Subject: <strong>{tpl.subject || "—"}</strong>
                </div>
                <div title="Preview — variables are resolved on send" style={{ border: "1px solid #e2e7e2", borderRadius: 8, padding: 12, maxHeight: 220, overflowY: "auto", background: "#fafbfa", fontSize: 13 }} dangerouslySetInnerHTML={{ __html: tpl.body_html || "" }} />
              </>
            );
          })()}
          <p style={{ fontSize: 12.5, color: "#64748b", lineHeight: 1.6, marginTop: 12 }}>
            The email will be sent to <strong>{view?.partner_email || "the customer"}</strong>. Delivery is asynchronous (mail worker, ~every 30 s) — track progress in CRM Settings → Outbox.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", borderTop: "1px solid #e2e8f0", paddingTop: 20, marginTop: 20 }}>
            <button onClick={() => setMailOpen(false)} className="ess-btn ess-btn-secondary" style={{ padding: "10px 24px" }}>Cancel</button>
            <button disabled={mailBusy || !mailTemplateId} onClick={handleSendEmail} className="ess-btn ess-btn-primary" style={{ padding: "10px 24px" }}>
              {mailBusy ? "Sending…" : "Send Email"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={waOpen} onClose={() => setWaOpen(false)} title={view ? `Send WhatsApp — ${view.name || ""}` : "Send WhatsApp"} size="lg">
        <div>
          <FormField label="Template" required>
            {waTemplates.length === 0 ? (
              <div style={{ fontSize: 13, color: "#94a3b8", padding: "9px 0" }}>No WhatsApp templates found for sales orders. Create one in CRM Settings → Templates.</div>
            ) : (
              <select value={waTemplateId} onChange={(e) => setWaTemplateId(e.target.value)} style={inputStyle}>
                <option value="">Select a template…</option>
                {waTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            )}
          </FormField>
          {(() => {
            const tpl = waTemplates.find((t) => String(t.id) === String(waTemplateId));
            if (!tpl) return null;
            return (
              <div title="Preview — variables are resolved on send" style={{ border: "1px solid #e2e7e2", borderRadius: 8, padding: 12, maxHeight: 220, overflowY: "auto", background: "#f0fdf4", fontSize: 13, color: "#334155", whiteSpace: "pre-wrap" }}>
                {tpl.body_text || "—"}
              </div>
            );
          })()}
          <p style={{ fontSize: 12.5, color: "#64748b", lineHeight: 1.6, marginTop: 12 }}>
            The message will be sent to <strong>{view?.partner_phone || "the customer"}</strong> via WhatsApp. Delivery is asynchronous — track progress in CRM Settings → WhatsApp tab.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", borderTop: "1px solid #e2e8f0", paddingTop: 20, marginTop: 20 }}>
            <button onClick={() => setWaOpen(false)} className="ess-btn ess-btn-secondary" style={{ padding: "10px 24px" }}>Cancel</button>
            <button disabled={waBusy || !waTemplateId} onClick={handleSendWhatsApp} className="ess-btn ess-btn-primary" style={{ padding: "10px 24px", background: "#166534" }}>
              {waBusy ? "Sending…" : "Send WhatsApp"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminPanelSalesOrdersPage;
